import { useState, useEffect } from "react"; 
import { motion, AnimatePresence } from "framer-motion";
import { Send, Shield, Clock, Star, CheckCircle, Users, ArrowRight, Flame, Zap, Phone, Lock, AlertTriangle } from "lucide-react";

import { submitFormAndRedirectToWhatsApp } from "@/lib/submitForm";

const urgencyMessages = [
  { icon: Flame, text: "3 créneaux disponibles cette semaine" },
  { icon: Zap, text: "Devis gratuit en moins de 24h" },
  { icon: CheckCircle, text: "+200 chantiers réalisés dans le 89" },
  { icon: AlertTriangle, text: "Demande en hausse – Réservez vite" },
];

const staggerItem = {
  hidden: { opacity: 0, x: -20 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.5 + i * 0.15, duration: 0.5, ease: "easeOut" as const },
  }),
};

const HeroSection = () => {
  const [formData, setFormData] = useState({ nom: "", telephone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [urgencyIdx, setUrgencyIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setUrgencyIdx((i) => (i + 1) % urgencyMessages.length), 3500);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitFormAndRedirectToWhatsApp({
      nom: formData.nom,
      telephone: formData.telephone,
      message: formData.message,
      source: "hero-accueil",
    });
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ nom: "", telephone: "", message: "" });
  };

  const socialProof = [
    { icon: CheckCircle, text: "Devis 100% gratuit, sans engagement" },
    { icon: Shield, text: "Assurance décennale & garantie qualité" },
    { icon: Users, text: "+200 clients satisfaits – Note 5/5 Google" },
  ];

  return (
    <section
      id="accueil"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Geometric background */}
      <div className="absolute inset-0 bg-secondary">
        <motion.div
          className="absolute inset-0 opacity-[0.07]"
          animate={{ backgroundPosition: ["0px 0px", "60px 60px"] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: [0.8, 1, 0.8], scale: [1, 1.05, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          style={{
            background: "radial-gradient(ellipse 80% 60% at 70% 50%, hsl(24 100% 40% / 0.15) 0%, transparent 70%)",
          }}
        />
        <motion.div
          className="absolute inset-0 opacity-[0.04]"
          animate={{ backgroundPosition: ["0px 0px", "56px 56px"] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          style={{
            backgroundImage: "repeating-linear-gradient(45deg, hsl(var(--primary)), hsl(var(--primary)) 1px, transparent 1px, transparent 40px)",
            backgroundSize: "56px 56px",
          }}
        />
        <motion.div
          className="absolute w-72 h-72 rounded-full blur-3xl"
          style={{ background: "hsl(24 100% 40% / 0.06)", top: "20%", left: "10%" }}
          animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-96 h-96 rounded-full blur-3xl"
          style={{ background: "hsl(24 100% 40% / 0.04)", bottom: "10%", right: "5%" }}
          animate={{ y: [0, 25, 0], x: [0, -15, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>

      <div className="container-custom w-full section-padding relative z-10">
        {/* Urgency ticker */}
        <AnimatePresence mode="wait">
          <motion.div
            key={urgencyIdx}
            initial={{ opacity: 0, y: -15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="mb-6 inline-block bg-primary/15 border border-primary/30 rounded-full px-5 py-2 animate-glow-border"
          >
            <span className="text-primary font-heading font-bold text-sm flex items-center gap-2">
              {(() => { const UrgIcon = urgencyMessages[urgencyIdx].icon; return <UrgIcon size={16} />; })()}
              {urgencyMessages[urgencyIdx].text}
            </span>
          </motion.div>
        </AnimatePresence>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="font-heading font-black text-3xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6">
              <motion.span
                className="text-secondary-foreground inline-block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                Artisan Peinture &
              </motion.span>
              <br />
              <motion.span
                className="text-gradient-primary inline-block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                Rénovation Habitat
              </motion.span>
              <br />
              <motion.span
                className="text-secondary-foreground inline-block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                à Sens (89)
              </motion.span>
            </h1>

            <motion.p
              className="text-base md:text-xl text-secondary-foreground/70 mb-5 md:mb-6 max-w-lg font-body"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              Peinture intérieure &amp; extérieure, démoussage toiture, hydrofuge façade et petite maçonnerie dans l'Yonne.
            </motion.p>

            {/* Social proof bullets */}
            <div className="space-y-2 mb-6 md:mb-8">
              {socialProof.map((item, i) => (
                <motion.div
                  key={item.text}
                  custom={i}
                  variants={staggerItem}
                  initial="hidden"
                  animate="visible"
                  className="flex items-center gap-2.5"
                >
                  <item.icon size={18} className="text-primary shrink-0" />
                  <span className="text-secondary-foreground/80 text-sm font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons - AGGRESSIVE */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <motion.a
                href="tel:+33373610242"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 md:px-8 py-3.5 md:py-4 rounded-xl font-heading font-bold text-base md:text-lg hover:brightness-110 transition-all animate-pulse-glow"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Phone size={18} className="animate-shake-subtle" /> Appeler maintenant
              </motion.a>
              <motion.a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary px-5 md:px-6 py-3.5 md:py-4 rounded-xl font-heading font-bold text-sm md:text-base hover:bg-primary hover:text-primary-foreground transition-all"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
              >
                Devis gratuit <ArrowRight size={16} className="animate-arrow-bounce" />
              </motion.a>
            </motion.div>

            {/* Micro social proof */}
            <motion.div
              className="flex items-center gap-3 mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.5 }}
            >
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.3 + i * 0.1, type: "spring" }}
                    className="w-8 h-8 rounded-full bg-primary/20 border-2 border-secondary flex items-center justify-center text-xs font-bold text-primary"
                  >
                    {["JP", "ML", "SC"][i - 1]}
                  </motion.div>
                ))}
              </div>
              <p className="text-secondary-foreground/60 text-xs">
                <strong className="text-secondary-foreground/80">Jean-Pierre, Marie-Laure</strong> et d'autres ont demandé un devis récemment
              </p>
            </motion.div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, y: 50, rotateX: 5 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            <motion.div
              className="bg-background/95 backdrop-blur-lg rounded-2xl p-5 md:p-8 relative overflow-hidden border-2 border-primary/20 animate-glow-border"
              style={{ boxShadow: "var(--shadow-card)" }}
            >
              {/* Urgency ribbon */}
              <motion.div
                className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-2 text-xs font-heading font-bold tracking-wide flex items-center justify-center gap-1.5"
                initial={{ y: -40 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.8, type: "spring" }}
              >
                <Zap size={14} className="animate-bounce-attention" /> RÉPONSE GARANTIE SOUS 24H
              </motion.div>

              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-1">
                  Devis Express <span className="text-primary">Gratuit</span>
                </h2>
                <p className="text-muted-foreground text-sm mb-5">Sans engagement • Estimation personnalisée</p>

                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-12"
                  >
                    <motion.div
                      className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 0.6 }}
                    >
                      <Shield className="text-primary" size={32} />
                    </motion.div>
                    <p className="font-heading font-bold text-lg text-foreground">Demande envoyée !</p>
                    <p className="text-muted-foreground text-sm mt-1">Nous vous recontactons sous 24h.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {[
                      { type: "text" as const, placeholder: "Votre nom", key: "nom", required: true },
                      { type: "tel" as const, placeholder: "Votre téléphone", key: "telephone", required: true },
                    ].map((field, i) => (
                      <motion.input
                        key={field.key}
                        type={field.type}
                        placeholder={field.placeholder}
                        required={field.required}
                        aria-label={field.placeholder}
                        value={formData[field.key as keyof typeof formData]}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.9 + i * 0.1 }}
                      />
                    ))}
                    <motion.textarea
                      placeholder="Décrivez votre projet (optionnel)..."
                      rows={2}
                      aria-label="Description du projet"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.1 }}
                    />
                    <motion.button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-4 rounded-xl font-heading font-bold text-lg hover:brightness-110 transition-all group animate-pulse-glow"
                      style={{ boxShadow: "var(--shadow-cta)" }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2 }}
                    >
                      <Send size={20} className="group-hover:translate-x-1 transition-transform" />
                      Recevoir mon devis gratuit
                    </motion.button>
                    <p className="text-center text-muted-foreground text-xs flex items-center justify-center gap-1">
                      <Lock size={12} /> Vos données restent confidentielles
                    </p>
                  </form>
                )}

                {/* Trust signals */}
                <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-border">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={14} className="text-primary fill-primary" />
                    ))}
                  </div>
                  <span className="text-muted-foreground text-xs">5/5 sur Google • +200 chantiers</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
